/**
 * My Watches — manage rule subscriptions + view the in-app alert feed.
 *
 * Two stacked panels:
 *   1) My Watches  — list of subscriptions with severity gate, snooze, unsub
 *   2) Recent Alerts — paginated feed of `alert_deliveries` (in_app channel)
 *
 * Email/Slack/Teams deliveries are listed as `queued` until those channel
 * handlers land in the next Phase 2B slice.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bell, BellOff, BellRing, Clock, RefreshCw, ExternalLink,
  AlertTriangle, CheckCircle2, ArrowUpRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Tabs, TabsList, TabsTrigger,
} from './ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SEVERITY_OPTIONS = [
  { value: 'any', label: 'Any change' },
  { value: 'medium', label: 'Medium or higher' },
  { value: 'high', label: 'High or higher' },
  { value: 'critical', label: 'Critical only' },
];

const EVENT_META = {
  new: { label: 'New', tone: 'bg-red-50 text-red-700 border-red-200', Icon: AlertTriangle },
  worsened: { label: 'Worsened', tone: 'bg-orange-50 text-orange-700 border-orange-200', Icon: ArrowUpRight },
  resolved: { label: 'Resolved', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
};

const fmtAgo = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const MyWatches = () => {
  const [watches, setWatches] = useState([]);
  const [feed, setFeed] = useState({ items: [], unread: 0 });
  const [loading, setLoading] = useState(true);
  const [feedTab, setFeedTab] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const [w, d] = await Promise.all([
        axios.get(`${API}/api/alerts/watches`),
        axios.get(`${API}/api/alerts/deliveries`, { params: { limit: 100 } }),
      ]);
      setWatches(w.data || []);
      setFeed(d.data || { items: [], unread: 0 });
    } catch (e) {
      toast.error('Could not load watches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateSeverity = async (watch, value) => {
    try {
      const { data } = await axios.post(`${API}/api/alerts/watches`, {
        scope: watch.scope,
        source: watch.source,
        rule_key: watch.rule_key,
        severity_min: value === 'any' ? null : value,
      });
      setWatches((arr) => arr.map((w) => (w.id === data.id ? data : w)));
      toast.success('Severity gate updated');
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const snooze = async (watch, days) => {
    try {
      const { data } = await axios.post(
        `${API}/api/alerts/watches/${watch.id}/snooze`,
        { days },
      );
      setWatches((arr) =>
        arr.map((w) => (w.id === watch.id ? { ...w, snoozed_until: data.snoozed_until } : w)),
      );
      toast.success(`Snoozed ${days}d`);
    } catch (e) {
      toast.error('Could not snooze');
    }
  };

  const unsnooze = async (watch) => {
    try {
      await axios.delete(`${API}/api/alerts/watches/${watch.id}/snooze`);
      setWatches((arr) =>
        arr.map((w) => (w.id === watch.id ? { ...w, snoozed_until: null } : w)),
      );
      toast.success('Un-snoozed');
    } catch (e) {
      toast.error('Could not un-snooze');
    }
  };

  const unsubscribe = async (watch) => {
    if (!window.confirm(`Stop watching ${watch.rule_key || 'this rule'}?`)) return;
    try {
      await axios.delete(`${API}/api/alerts/watches/${watch.id}`);
      setWatches((arr) => arr.filter((w) => w.id !== watch.id));
      toast.success('Unsubscribed');
    } catch (e) {
      toast.error('Could not unsubscribe');
    }
  };

  const markRead = async (deliveryId) => {
    try {
      await axios.post(`${API}/api/alerts/deliveries/${deliveryId}/read`);
      setFeed((f) => ({
        ...f,
        items: f.items.map((d) => (d.id === deliveryId ? { ...d, read: true } : d)),
        unread: Math.max(0, f.unread - 1),
      }));
    } catch (e) {
      /* swallow */
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/api/alerts/deliveries/read-all`);
      setFeed((f) => ({ ...f, items: f.items.map((d) => ({ ...d, read: true })), unread: 0 }));
    } catch (e) {
      /* swallow */
    }
  };

  const feedFiltered = useMemo(() => {
    if (feedTab === 'unread') return feed.items.filter((d) => !d.read);
    return feed.items;
  }, [feed.items, feedTab]);

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 space-y-6" data-testid="my-watches-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">My Watches</h1>
        <p className="text-sm text-slate-600 mt-1">
          Subscribe to compliance findings. We'll notify you in-app the moment a
          watched rule changes — new, worsened, or resolved.
        </p>
      </div>

      {/* Watches list */}
      <Card data-testid="my-watches-list">
        <CardHeader className="flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BellRing className="w-5 h-5 text-indigo-600" />
              Subscriptions
              <Badge className="bg-indigo-100 text-indigo-700">{watches.length}</Badge>
            </CardTitle>
            <CardDescription>
              Bell icon on any finding subscribes you to that rule.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="watches-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {watches.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">
              You're not watching any rules yet. Open a posture page and click the
              <Bell className="inline w-3.5 h-3.5 mx-1" /> bell on a finding to subscribe.
            </p>
          ) : (
            <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
              {watches.map((w) => {
                const isSnoozed = w.snoozed_until && new Date(w.snoozed_until).getTime() > Date.now();
                return (
                  <div
                    key={w.id}
                    className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                    data-testid={`watch-row-${w.rule_key || w.scope}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {w.scope === 'workspace' ? 'All findings (workspace-wide)' : w.rule_key}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px] text-slate-500">
                        <Badge className="bg-slate-100 text-slate-700 font-normal">
                          {w.source || 'all sources'}
                        </Badge>
                        {isSnoozed && (
                          <span className="inline-flex items-center text-amber-700">
                            <Clock className="w-3 h-3 mr-1" />
                            Snoozed until {new Date(w.snoozed_until).toLocaleDateString()}
                          </span>
                        )}
                        {(w.channels || []).map((c) => (
                          <span key={c.type} className="text-slate-400">
                            · {c.type}{c.type === 'email' ? ' (queued)' : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select
                        value={w.severity_min || 'any'}
                        onValueChange={(v) => updateSeverity(w, v)}
                      >
                        <SelectTrigger className="h-8 w-[180px] text-xs" data-testid={`watch-severity-${w.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SEVERITY_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {isSnoozed ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => unsnooze(w)}
                          data-testid={`watch-unsnooze-${w.id}`}
                        >
                          <Bell className="w-3 h-3 mr-1" />
                          Un-snooze
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => snooze(w, 7)}
                          data-testid={`watch-snooze-${w.id}`}
                        >
                          <Clock className="w-3 h-3 mr-1" />
                          Snooze 7d
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => unsubscribe(w)}
                        data-testid={`watch-unsubscribe-${w.id}`}
                      >
                        <BellOff className="w-3 h-3 mr-1" />
                        Unsubscribe
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent alerts feed */}
      <Card data-testid="my-alerts-feed">
        <CardHeader className="flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-slate-700" />
              Recent Alerts
              {feed.unread > 0 && (
                <Badge className="bg-red-100 text-red-700">{feed.unread} unread</Badge>
              )}
            </CardTitle>
            <CardDescription>
              In-app feed of your watch deliveries. Email delivery lands in the next release.
            </CardDescription>
          </div>
          {feed.unread > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={markAllRead}
              data-testid="alerts-mark-all-read"
            >
              Mark all read
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <Tabs value={feedTab} onValueChange={setFeedTab} className="mb-3">
            <TabsList>
              <TabsTrigger value="all" data-testid="feed-tab-all">All</TabsTrigger>
              <TabsTrigger value="unread" data-testid="feed-tab-unread">
                Unread <Badge className="ml-2 bg-slate-200 text-slate-700">{feed.unread}</Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {feedFiltered.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">
              {feedTab === 'unread' ? 'Nothing unread.' : 'No alerts yet.'}
            </p>
          ) : (
            <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
              {feedFiltered.map((d) => {
                const meta = EVENT_META[d.event_type] || EVENT_META.new;
                const Icon = meta.Icon;
                return (
                  <div
                    key={d.id}
                    className={`flex items-start gap-3 px-4 py-3 ${d.read ? 'opacity-70' : ''}`}
                    data-testid={`alert-delivery-${d.id}`}
                  >
                    <div className={`mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full border ${meta.tone}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-slate-900">{d.subject}</span>
                        <Badge className={meta.tone}>{meta.label}</Badge>
                        {d.channel_type !== 'in_app' && (
                          <Badge className="bg-slate-100 text-slate-600 text-[10px]">
                            {d.channel_type}{d.state === 'queued' ? ' · queued' : ''}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {fmtAgo(d.created_at)} · rule <code className="text-[11px]">{d.rule_key}</code>
                      </p>
                      {d.body?.what_would_fix && (
                        <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                          <span className="font-medium">Fix:</span> {d.body.what_would_fix}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {!d.read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-[11px]"
                          onClick={() => markRead(d.id)}
                          data-testid={`alert-mark-read-${d.id}`}
                        >
                          Mark read
                        </Button>
                      )}
                      {d.source === 'google_workspace' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-[11px] text-slate-500"
                          asChild
                        >
                          <a href="/connections/google-workspace" data-testid={`alert-jump-${d.id}`}>
                            View
                            <ExternalLink className="w-3 h-3 ml-1" />
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyWatches;
